import { none } from "fp-ts/lib/Option";
import { AssetType, ImageWithFocalPointType, VideoType } from "@swo/shared";
import { pipe } from "fp-ts/lib/function";
import { array, eq, nonEmptyArray, option } from "fp-ts";
import { nonEmpty } from "../../../utils/fp/small-fp-utils";
import { Eq } from "fp-ts/Eq";
import * as S from "fp-ts/string";
import { PDPImagesGridImageT } from "../PDP_Gallery/types";
import { PDPSwarovskiProductFrontend, PDPSwarovskiProductVariantFrontend } from "./state/types";

export type PDPGalleryProp =
  | {
      primaryImage: PDPImagesGridImageT | undefined;
      scrollingGalleryElements: Array<PDPImagesGridImageT | VideoType>;
      hasLifestyleAsset: boolean;
    }
  | undefined;

const galleryElemEq: Eq<PDPImagesGridImageT | VideoType> = pipe(
  S.Eq,
  eq.contramap(b => {
    if (b.__typename === "Image") {
      return b.url;
    } else {
      return b.video.url;
    }
  }),
);

export function getGalleryProps(
  selectedProduct: Pick<PDPSwarovskiProductFrontend, "images">,
  selectedVariant: PDPSwarovskiProductVariantFrontend | null,
  limit: number,
): PDPGalleryProp {
  const productCarouselImages = pipe(
    [
      ...(selectedProduct.images.thumbnail ? [selectedProduct.images.thumbnail] : []),
      ...selectedProduct.images.gallery,
    ],
    nonEmptyArray.fromArray,
  );

  return pipe(
    selectedVariant,
    option.fromNullable,
    option.fold(
      () => productCarouselImages,
      selectedVariant =>
        pipe(
          [
            ...(selectedVariant.images.thumbnail ? [selectedVariant.images.thumbnail] : []),
            ...selectedVariant.images.gallery,
          ],
          nonEmptyArray.fromArray,
          option.alt(() => productCarouselImages),
        ),
    ),
    option.chain(arr =>
      pipe(
        arr,
        array.filterMap<AssetType | ImageWithFocalPointType | VideoType, PDPImagesGridImageT | VideoType>(elem => {
          if (elem.__typename === "UtilityVideo") {
            return option.some(elem);
          }

          const image = elem.__typename === "UtilityImageWithFocalPoint" ? elem.image : elem;
          const { width, height } = image;

          if (typeof width === "number" && typeof height === "number" && height > 0 && width > 0) {
            const focalPoint = elem.__typename === "UtilityImageWithFocalPoint" ? elem.focalPoint : undefined;

            return option.some({
              __typename: "Image" as const,
              url: image.url,
              sys: {
                id: image.sys.id,
              },
              title: image.title,
              description: image.description || undefined,
              width: width,
              height: height,
              focalPoint,
              isLifestyleAsset: !!image?.isLifestyleAsset,
            });
          } else {
            return none;
          }
        }),
        array.uniq(galleryElemEq),
        array.takeLeft(limit),
        nonEmptyArray.fromArray,
        option.map(elements => {
          const hasLifestyleAsset = elements.some(e => e.isLifestyleAsset);

          const primaryImage = elements.map(e => (e.__typename === "Image" ? e : null)).filter(nonEmpty)[0];

          const scrollingGalleryElements = elements.filter(e => e.__typename != "Image" || e.url !== primaryImage?.url);

          return {
            primaryImage,
            scrollingGalleryElements,
            hasLifestyleAsset,
          };
        }),
      ),
    ),
    option.toUndefined,
  );
}
